import React from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  Typography,
  useTheme,
} from "@mui/material";
import { CrossIcon } from "../../assets/Icon/ModalIcon";
import { PreviewIcon, DocumentUploadIcon } from "../../assets/Icon/dashboredIcon";
import styled from "styled-components";

interface MemberDetailModalProps {
  open: boolean;
  onClose: () => void;
  member: any;
}

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "600px",
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
  padding: "32px",
  maxHeight: "90vh",
  overflowY: "auto",
  "@media (max-width: 600px)": {
    width: "90%",
    padding: "20px",
  },
}));

const DetailRow = ({ label, value }: { label: string; value: any }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ color: "#6C6F71", fontWeight: 500, fontSize: "12px", textTransform: "uppercase" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ color: "#01060B", fontWeight: 500, mt: 0.5 }}>
      {value || "—"}
    </Typography>
  </Box>
);

const MemberDetailModal: React.FC<MemberDetailModalProps> = ({ open, onClose, member }) => {
  const theme = useTheme();
  const baseUrl = process.env.REACT_APP_BASE_IMAGE_URL || "";

  if (!member) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <StyledModalBox>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#2977CC" }}>
            Member Details
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CrossIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <DetailRow label="First Name" value={member.first_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Middle Name" value={member.middle_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Last Name" value={member.last_name} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Email" value={member.email} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Phone" value={member.phone_number} />
          </Grid>
          <Grid item xs={12} sm={6}>
             <DetailRow label="Country Code" value={member.country_code} />
          </Grid>
          <Grid item xs={12}>
            <DetailRow label="Address" value={member.address} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="City" value={member.city} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="State" value={member.state} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Zip Code" value={member.zip_code} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DetailRow label="Country" value={member.country_name || member.country} />
          </Grid>

          {/* Passport Section */}
          <Grid item xs={12}>
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              borderRadius: "8px", 
              border: "1px solid #EAF1FA",
              backgroundColor: "#F9FBFE",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Passport Scan
                </Typography>
                <Typography variant="caption" sx={{ color: "#6C6F71" }}>
                  {member.scan_passport ? "Attached" : "No document attached"}
                </Typography>
              </Box>
              {member.scan_passport && (
                <Box display="flex" gap={1}>
                  <IconButton 
                    onClick={() => window.open(`${baseUrl}${member.scan_passport}`, "_blank")}
                    sx={{ color: "#2977CC" }}
                  >
                    <PreviewIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = `${baseUrl}${member.scan_passport}`;
                      link.download = `passport_${member.first_name}.pdf`;
                      link.click();
                    }}
                    sx={{ color: "#2977CC" }}
                  >
                    <DocumentUploadIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Box mt={4} display="flex" justifyContent="flex-end">
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              background: "linear-gradient(98.24deg, #2977CC 4.13%, #1D538F 128.24%)",
              textTransform: "none",
              px: 4
            }}
          >
            Close
          </Button>
        </Box>
      </StyledModalBox>
    </Modal>
  );
};

export default MemberDetailModal;
